import { ComponentType } from 'react'
import {
  UsersIcon,
  HeartIcon,
  PaletteIcon,
  TrophyIcon,
  BriefcaseIcon,
  FlameIcon,
} from 'lucide-react'

export interface SocialCategory {
  id: string
  label: string
  labelEs: string
  icon: ComponentType<{ className?: string }>
  gradient: string
  borderColor: string
  accentColor: string
}

export const CATEGORIES: SocialCategory[] = [
  {
    id: 'community',
    label: 'Community Advocacy',
    labelEs: 'Defensa Comunitaria',
    icon: UsersIcon,
    gradient: 'from-haa-blue to-haa-navy',
    borderColor: 'border-haa-blue',
    accentColor: 'text-haa-blue',
  },
  {
    id: 'culture',
    label: 'Culture & Lifestyle',
    labelEs: 'Cultura y Estilo de Vida',
    icon: HeartIcon,
    gradient: 'from-pink-500 to-rose-600',
    borderColor: 'border-pink-500',
    accentColor: 'text-pink-600',
  },
  {
    id: 'creatives',
    label: 'Creatives & Brand Builders',
    labelEs: 'Creativos y Constructores de Marca',
    icon: PaletteIcon,
    gradient: 'from-violet-500 to-purple-700',
    borderColor: 'border-violet-500',
    accentColor: 'text-violet-600',
  },
  {
    id: 'sports',
    label: 'Athletes & Sports',
    labelEs: 'Atletas y Deportes',
    icon: TrophyIcon,
    gradient: 'from-orange-500 to-red-600',
    borderColor: 'border-orange-500',
    accentColor: 'text-orange-600',
  },
  {
    id: 'entrepreneurs',
    label: 'Entrepreneurs',
    labelEs: 'Emprendedores',
    icon: BriefcaseIcon,
    gradient: 'from-emerald-500 to-teal-700',
    borderColor: 'border-emerald-500',
    accentColor: 'text-emerald-600',
  },
  {
    id: 'changemakers',
    label: 'Changemakers',
    labelEs: 'Agentes de Cambio',
    icon: FlameIcon,
    gradient: 'from-amber-500 to-orange-600',
    borderColor: 'border-amber-500',
    accentColor: 'text-amber-600',
  },
]

export interface SocialAccount {
  name: string
  handle: string
  platform: 'Instagram' | 'TikTok' | 'YouTube'
  categoryId: string
  bio: string
  bioEs: string
  url: string
  imageGradient: string
}

export const accounts: SocialAccount[] = [
  {
    name: 'Honduran American Association',
    handle: '@honduranamerican',
    platform: 'Instagram',
    categoryId: 'community',
    bio: 'Elevating the Honduran diaspora in the United States through advocacy, civic engagement, and community empowerment.',
    bioEs:
      'Elevando a la diáspora hondureña en los Estados Unidos a través de la defensa, la participación cívica y el empoderamiento comunitario.',
    url: 'https://www.instagram.com/honduranamerican/',
    imageGradient: 'from-haa-blue to-haa-navy',
  },
]

export interface FeaturedCreator {
  name: string
  handle: string
  bio: string
  bioEs: string
  imageGradient: string
  featuredReason: string
  featuredReasonEs: string
  instagramUrl?: string
  tiktokUrl?: string
  youtubeUrl?: string
  spotifyUrl?: string
  xUrl?: string
  facebookUrl?: string
  embedUrl?: string
}

export const featuredCreators: FeaturedCreator[] = [
  {
    name: 'Rocsi Diaz',
    handle: '@rocsidiaz',
    bio: 'Honduran-American television host, producer, and media personality known for her work on BET\u2019s "106 & Park" and Entertainment Tonight, championing Latino representation in entertainment and lifestyle media.',
    bioEs:
      'Presentadora, productora y personalidad de medios hondureño-americana reconocida por su trabajo en "106 & Park" de BET y Entertainment Tonight, defendiendo la representación latina en los medios de entretenimiento y estilo de vida.',
    imageGradient: 'from-fuchsia-500 to-rose-600',
    featuredReason:
      'A trailblazing Honduran voice in American entertainment and lifestyle media.',
    featuredReasonEs:
      'Una voz hondureña pionera en los medios de entretenimiento y estilo de vida en Estados Unidos.',
    instagramUrl: 'https://www.instagram.com/rocsidiaz/',
  },
  {
    name: 'Dunia Elvir',
    handle: '@duniaelvir',
    bio: 'Honduran-American Emmy Award-winning journalist and longtime news anchor at Telemundo 52 in Los Angeles, recognized for her decades of bilingual reporting and her advocacy for the Latino community.',
    bioEs:
      'Periodista hondureño-americana ganadora del Emmy y presentadora de noticias por muchos años en Telemundo 52 en Los Ángeles, reconocida por sus décadas de cobertura bilingüe y su defensa de la comunidad latina.',
    imageGradient: 'from-sky-500 to-indigo-700',
    featuredReason:
      'A trusted Honduran-American voice in U.S. Spanish-language broadcast journalism.',
    featuredReasonEs:
      'Una voz hondureño-americana de confianza en el periodismo televisivo en español en Estados Unidos.',
    instagramUrl: 'https://www.instagram.com/duniaelvir/',
  },
  {
    name: 'Teófimo López',
    handle: '@teofimolopez',
    bio: 'Honduran-American world champion boxer, unifying titles across weight classes and inspiring the next generation of fighters with Honduran pride.',
    bioEs:
      'Boxeador hondureño-americano campeón mundial, unificando títulos en varias divisiones e inspirando a la próxima generación de peleadores con orgullo hondureño.',
    imageGradient: 'from-red-600 to-yellow-500',
    featuredReason:
      'A world champion carrying the Honduran flag at the highest level of boxing.',
    featuredReasonEs:
      'Un campeón mundial portando la bandera hondureña al más alto nivel del boxeo.',
    instagramUrl: 'https://www.instagram.com/teofimolopez/',
    embedUrl: 'https://www.instagram.com/teofimolopez/embed/',
  },
  {
    name: 'Satcha Pretto',
    handle: '@satchapretto',
    bio: 'Honduran-American journalist and co-host of Univision\u2019s "Despierta América," inspiring millions of Latinos every morning with news, storytelling, and community advocacy.',
    bioEs:
      'Periodista hondureño-americana y co-presentadora de "Despierta América" de Univision, inspirando a millones de latinos cada mañana con noticias, narración y defensa comunitaria.',
    imageGradient: 'from-rose-500 to-amber-600',
    featuredReason:
      'A leading Honduran voice on national Spanish-language television.',
    featuredReasonEs:
      'Una voz hondureña líder en la televisión nacional en español.',
    instagramUrl: 'https://www.instagram.com/satchapretto/',
    embedUrl: 'https://www.instagram.com/satchapretto/embed/',
  },
  {
    name: 'Mauricio Dubón',
    handle: '@mauriciod10',
    bio: 'Honduran Major League Baseball player and World Series champion with the Houston Astros, representing Honduras on the world stage.',
    bioEs:
      'Beisbolista hondureño de las Grandes Ligas y campeón de la Serie Mundial con los Houston Astros, representando a Honduras en el escenario mundial.',
    imageGradient: 'from-orange-500 to-red-700',
    featuredReason:
      'Honduras\u2019 first World Series champion, inspiring a generation of athletes.',
    featuredReasonEs:
      'El primer campeón hondureño de la Serie Mundial, inspirando a una generación de atletas.',
    instagramUrl: 'https://www.instagram.com/mauriciod10/',
    embedUrl: 'https://www.instagram.com/mauriciod10/embed/',
  },
  {
    name: 'Jonathan Mejia',
    handle: '@jonathanjmejia',
    bio: 'Honduran-American content creator sharing culture, lifestyle, and community stories that connect the diaspora.',
    bioEs:
      'Creador de contenido hondureño-americano compartiendo cultura, estilo de vida e historias comunitarias que conectan a la diáspora.',
    imageGradient: 'from-sky-500 to-indigo-700',
    featuredReason:
      'Representing Honduran-American culture and community on social media.',
    featuredReasonEs:
      'Representando la cultura y la comunidad hondureño-americana en las redes sociales.',
    instagramUrl: 'https://www.instagram.com/jonathanjmejia/',
    embedUrl: 'https://www.instagram.com/jonathanjmejia/embed/',
  },
  {
    name: 'Maity Interiano',
    handle: '@maityinteriano',
    bio: 'Honduran-American journalist and television host at Univision, inspiring millions with her storytelling, advocacy, and representation of the Latino community.',
    bioEs:
      'Periodista y presentadora de televisión hondureño-americana en Univision, inspirando a millones con su narración, defensa y representación de la comunidad latina.',
    imageGradient: 'from-rose-400 to-pink-700',
    featuredReason:
      'A leading Honduran voice in U.S. Spanish-language media and journalism.',
    featuredReasonEs:
      'Una voz hondureña líder en los medios y el periodismo en español de EE.UU.',
    instagramUrl: 'https://www.instagram.com/maityinteriano/',
    embedUrl: 'https://www.instagram.com/maityinteriano/embed/',
  },
  {
    name: 'Monse Medina',
    handle: '@monsemedinaradio',
    bio: 'Honduran-American radio host and media personality sharing stories, music, and conversations that connect the Latino community.',
    bioEs:
      'Locutora y personalidad de medios hondureño-americana compartiendo historias, música y conversaciones que conectan a la comunidad latina.',
    imageGradient: 'from-rose-500 to-fuchsia-700',
    featuredReason:
      'Amplifying Honduran-American voices through radio and storytelling.',
    featuredReasonEs:
      'Amplificando las voces hondureño-americanas a través de la radio y la narración.',
    instagramUrl: 'https://www.instagram.com/monsemedinaradio/',
    embedUrl: 'https://www.instagram.com/monsemedinaradio/embed/',
  },
  {
    name: 'MarieBelle',
    handle: '@mariebelleofficial',
    bio: 'Honduran-born luxury chocolatier and founder of MarieBelle New York, crafting world-renowned artisanal chocolates and bringing Honduran heritage to the global stage.',
    bioEs:
      'Chocolatera de lujo nacida en Honduras y fundadora de MarieBelle New York, creando chocolates artesanales de renombre mundial y llevando la herencia hondureña al escenario global.',
    imageGradient: 'from-amber-700 to-rose-900',
    featuredReason:
      'A globally celebrated Honduran chocolatier elevating Honduran heritage through luxury craftsmanship.',
    featuredReasonEs:
      'Una chocolatera hondureña celebrada mundialmente, elevando la herencia hondureña a través de la artesanía de lujo.',
    instagramUrl: 'https://www.instagram.com/mariebelleofficial/',
    embedUrl: 'https://www.instagram.com/mariebelleofficial/embed/',
  },
  {
    name: 'PerquinJC',
    handle: '@perquinjc',
    bio: 'Honduran content creator sharing culture, community, and everyday stories that connect the diaspora to their roots.',
    bioEs:
      'Creador de contenido hondureño compartiendo cultura, comunidad e historias cotidianas que conectan a la diáspora con sus raíces.',
    imageGradient: 'from-haa-blue to-haa-skyblue',
    featuredReason: 'Bringing Honduran culture and community stories to life.',
    featuredReasonEs:
      'Dando vida a la cultura y las historias comunitarias hondureñas.',
    instagramUrl: 'https://www.instagram.com/perquinjc/',
    embedUrl: 'https://www.instagram.com/perquinjc/embed/',
  },
  {
    name: 'Don Ryvcko',
    handle: '@donryvcko',
    bio: 'Honduran-American content creator sharing culture, comedy, and community stories that resonate with the diaspora.',
    bioEs:
      'Creador de contenido hondureño-americano compartiendo cultura, comedia e historias comunitarias que resuenan con la diáspora.',
    imageGradient: 'from-orange-500 to-red-600',
    featuredReason: 'Amplifying Honduras through music.',
    featuredReasonEs: 'Amplificando Honduras a través de la música.',
    instagramUrl: 'https://www.instagram.com/donryvcko/',
    tiktokUrl: 'https://www.tiktok.com/@donryvcko',
    youtubeUrl: 'https://www.youtube.com/@donryvcko',
    spotifyUrl: 'https://open.spotify.com/artist/4sPaDVtUm4TRrM6TATYC8D',
    embedUrl: 'https://www.instagram.com/donryvcko/embed/',
  },
  {
    name: 'Shin Fujiyama',
    handle: '@shinfujiyama',
    bio: 'Philanthropist building 1,000 schools in Honduras. Sharing stories of resilience, community, and the power of education.',
    bioEs:
      'Filántropo construyendo 1,000 escuelas en Honduras. Compartiendo historias de resiliencia, comunidad y el poder de la educación.',
    imageGradient: 'from-haa-skyblue to-haa-midblue',
    featuredReason:
      'Inspiring millions by highlighting Honduras and building schools.',
    featuredReasonEs:
      'Inspirando a millones al destacar Honduras y construir escuelas.',
    instagramUrl: 'https://www.instagram.com/shinfujiyama/',
    tiktokUrl: 'https://www.tiktok.com/@shinfujiyama',
    youtubeUrl: 'https://www.youtube.com/@shinfujiyamaReal',
    embedUrl: 'https://www.instagram.com/shinfujiyama/embed/',
  },
  {
    name: 'Cocina Con Yesi',
    handle: '@yesirdgz98',
    bio: 'Honduran food creator sharing authentic recipes, traditional dishes, and the flavors of Honduras with nearly 700K followers.',
    bioEs:
      'Creadora de contenido de comida hondureña compartiendo recetas auténticas, platos tradicionales y los sabores de Honduras con casi 700K seguidores.',
    imageGradient: 'from-amber-400 to-red-500',
    featuredReason:
      'Bringing Honduran cuisine to the world, one recipe at a time.',
    featuredReasonEs:
      'Llevando la cocina hondureña al mundo, una receta a la vez.',
    tiktokUrl: 'https://www.tiktok.com/@yesirdgz98',
    facebookUrl: 'https://www.facebook.com/profile.php?id=61571612646796',
    youtubeUrl: 'https://www.youtube.com/@cocinaconyesi504',
    embedUrl: 'https://www.tiktok.com/embed/v2/7583394572437294366',
  },
  {
    name: 'David Ruiz',
    handle: '@davidruizz41',
    bio: 'Honduran professional soccer player connecting fans to the beautiful game and representing Honduras on the world stage.',
    bioEs:
      'Futbolista profesional hondureño conectando a los aficionados con el deporte y representando a Honduras en el escenario mundial.',
    imageGradient: 'from-blue-500 to-blue-800',
    featuredReason: 'Representing Honduras in professional soccer worldwide.',
    featuredReasonEs:
      'Representando a Honduras en el fútbol profesional mundial.',
    instagramUrl: 'https://www.instagram.com/davidruizz41/',
    tiktokUrl: 'https://www.tiktok.com/@davidruizz41',
    embedUrl: 'https://www.instagram.com/davidruizz41/embed/',
  },
  {
    name: 'Neida Sandoval',
    handle: '@neidasandovaltv',
    bio: 'Award-winning Honduran-American journalist and TV personality inspiring the community through storytelling and media.',
    bioEs:
      'Galardonada periodista y personalidad de televisión hondureño-americana inspirando a la comunidad a través de la narración y los medios.',
    imageGradient: 'from-rose-500 to-pink-700',
    featuredReason: 'A trailblazing Honduran voice in American media.',
    featuredReasonEs:
      'Una voz hondureña pionera en los medios estadounidenses.',
    instagramUrl: 'https://www.instagram.com/neidasandovaltv/',
    youtubeUrl: 'https://www.youtube.com/@neidasandovaltv',
    tiktokUrl: 'https://www.tiktok.com/@neidasandovaltv',
    facebookUrl: 'https://www.facebook.com/NeidaSandovalOficial',
    xUrl: 'https://x.com/NeidaSandoval',
    embedUrl: 'https://www.instagram.com/neidasandovaltv/embed/',
  },
  {
    name: 'Haley Amaya',
    handle: '@haleeyamaya',
    bio: 'Honduran-American model and content creator based in LA, sharing culture, soccer, and lifestyle with the diaspora.',
    bioEs:
      'Modelo y creadora de contenido hondureño-americana en LA, compartiendo cultura, fútbol y estilo de vida con la diáspora.',
    imageGradient: 'from-sky-400 to-indigo-600',
    featuredReason: 'Representing Honduras in modeling and lifestyle content.',
    featuredReasonEs:
      'Representando a Honduras en modelaje y contenido de estilo de vida.',
    instagramUrl: 'https://www.instagram.com/haleeyamaya/',
    tiktokUrl: 'https://www.tiktok.com/@haleeyamaya',
    embedUrl: 'https://www.instagram.com/haleeyamaya/embed/',
  },
  {
    name: 'JaycoSet',
    handle: '@jaycoset_',
    bio: 'Honduran-American content creator sharing culture, style, and everyday life with the community.',
    bioEs:
      'Creador de contenido hondureño-americano compartiendo cultura, estilo y vida cotidiana con la comunidad.',
    imageGradient: 'from-teal-500 to-cyan-700',
    featuredReason: 'Showcasing Honduran-American culture and humor.',
    featuredReasonEs: 'Mostrando la cultura y el humor hondureño-americano.',
    instagramUrl: 'https://www.instagram.com/jaycoset_/',
    tiktokUrl: 'https://www.tiktok.com/@jaycoset?lang=en',
    embedUrl: 'https://www.instagram.com/jaycoset_/embed/',
  },
  {
    name: 'Garifunarobics',
    handle: '@garifunarobics',
    bio: 'Getting people to dance and exercise with Honduran beats.',
    bioEs:
      'Haciendo que la gente baile y haga ejercicio con ritmos hondureños.',
    imageGradient: 'from-amber-500 to-orange-600',
    featuredReason: 'Getting people to dance and exercise with Honduran beats.',
    featuredReasonEs:
      'Haciendo que la gente baile y haga ejercicio con ritmos hondureños.',
    instagramUrl: 'https://www.instagram.com/garifunarobics/',
    tiktokUrl: 'https://www.tiktok.com/@garifunarobics',
    embedUrl: 'https://www.instagram.com/garifunarobics/embed/',
  },
  {
    name: 'Rosalila NYC',
    handle: '@rosalila.nyc',
    bio: 'Honduran-American creative platform celebrating art, culture, and community from New York City.',
    bioEs:
      'Plataforma creativa hondureño-americana celebrando arte, cultura y comunidad desde Nueva York.',
    imageGradient: 'from-rose-400 to-fuchsia-600',
    featuredReason: 'Celebrating Honduran art and culture from NYC.',
    featuredReasonEs: 'Celebrando el arte y la cultura hondureña desde NYC.',
    instagramUrl: 'https://www.instagram.com/rosalila.nyc/',
    embedUrl: 'https://www.instagram.com/rosalila.nyc/embed/',
  },
  {
    name: 'Honduras Futbol TV',
    handle: '@hnfutboltv',
    bio: 'Your go-to source for Honduran football coverage, highlights, and analysis. Bringing the passion of Honduran soccer to fans everywhere.',
    bioEs:
      'Tu fuente principal de cobertura, resúmenes y análisis del fútbol hondureño. Llevando la pasión del fútbol catracho a los aficionados en todas partes.',
    imageGradient: 'from-emerald-500 to-green-700',
    featuredReason:
      'Amplifying Honduran up-and-coming soccer talent around the world.',
    featuredReasonEs:
      'Amplificando el talento emergente del fútbol hondureño alrededor del mundo.',
    instagramUrl: 'https://www.instagram.com/hnfutboltv/',
    youtubeUrl: 'https://www.youtube.com/@HondurasFutbolTV',
    xUrl: 'https://x.com/hnfutboltv',
    embedUrl: 'https://www.instagram.com/hnfutboltv/embed/',
  },
  {
    name: 'Artisan Bryan',
    handle: '@artisanbryan',
    bio: 'Honduran-American artist and creative crafting handmade work that celebrates heritage, culture, and storytelling through art.',
    bioEs:
      'Artista y creativo hondureño-americano elaborando obras hechas a mano que celebran la herencia, la cultura y la narración a través del arte.',
    imageGradient: 'from-violet-500 to-purple-700',
    featuredReason:
      'Showcasing Honduran-American artistry and handmade craftsmanship.',
    featuredReasonEs:
      'Mostrando el arte y la artesanía hecha a mano hondureño-americana.',
    instagramUrl: 'https://www.instagram.com/artisanbryan/',
    embedUrl: 'https://www.instagram.com/artisanbryan/embed/',
  },
  {
    name: 'Elite Fam Jay',
    handle: '@elitefamjay',
    bio: 'Honduran-American creator sharing culture, community, and everyday moments that connect the diaspora.',
    bioEs:
      'Creador hondureño-americano compartiendo cultura, comunidad y momentos cotidianos que conectan a la diáspora.',
    imageGradient: 'from-haa-midblue to-haa-navy',
    featuredReason:
      'Bringing Honduran-American culture and community to a growing audience.',
    featuredReasonEs:
      'Llevando la cultura y la comunidad hondureño-americana a una audiencia creciente.',
    instagramUrl: 'https://www.instagram.com/elitefamjay/',
    embedUrl: 'https://www.instagram.com/elitefamjay/embed/',
  },
]
